import React, { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Camera, ScanLine, Loader2, X, Sparkles, Ruler, Activity, ShieldAlert, TrendingDown, TrendingUp, ChevronRight } from "lucide-react";
import { describeError } from "@/lib/api";
import {
  WOUND_ENGINES, deriveWoundContext, submitWoundAnalysis, pollWoundJob,
  listSessionWounds, woundTimeline, fileToBase64,
} from "@/lib/wound";

const pct = (f) => (f == null ? null : `${Math.round(f * 1000) / 10}%`);
const fmtTime = (iso) => {
  if (!iso) return "";
  try { return new Date(iso).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }); }
  catch { return iso; }
};
const num = (v) => { const n = parseFloat(v); return Number.isFinite(n) ? n : undefined; };

const is422 = (e) => e?.response?.status === 422 ||
  /content[- ]?safety|blocked|switch engine|422/i.test(describeError(e) || e?.message || "");

export default function WoundCare({ ctx, sessionId, transcript, patientName, onCountChange }) {
  const [files, setFiles] = useState([]);            // { base64, preview }
  const [woundSite, setWoundSite] = useState("");
  const [context, setContext] = useState("");
  const [label, setLabel] = useState("");
  const [engine, setEngine] = useState("premium");
  const [refMarker, setRefMarker] = useState("");
  const [mL, setML] = useState(""); const [mW, setMW] = useState(""); const [mD, setMD] = useState("");
  const [showManual, setShowManual] = useState(false);

  const [deriving, setDeriving] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [blocked, setBlocked] = useState(false);      // 422 content-safety
  const [analyses, setAnalyses] = useState([]);
  const [groups, setGroups] = useState([]);
  const [active, setActive] = useState(null);        // analysis open in the review modal
  const camRef = useRef(null); const fileRef = useRef(null);

  const refresh = useCallback(() => {
    if (sessionId) listSessionWounds(sessionId).then((a) => { setAnalyses(a); onCountChange?.(a.length); }).catch(() => {});
    // DQ-14: pass chart_uuid + client_id (not just MRN) so the scoped timeline resolves for chartless/MRN-less patients.
    if (ctx?.patientId || ctx?.mrn || ctx?.clientId) woundTimeline(ctx?.mrn, ctx?.patientId, ctx?.clientId).then(setGroups).catch(() => {});
  }, [sessionId, ctx?.mrn, ctx?.patientId, ctx?.clientId, onCountChange]);

  useEffect(() => { refresh(); }, [refresh]);

  const addFiles = async (fileList) => {
    const incoming = Array.from(fileList || []).filter((f) => f.type.startsWith("image/"));
    if (!incoming.length) return;
    const room = 4 - files.length;
    if (room <= 0) { toast.error("Up to 4 photos of the same wound."); return; }
    try {
      const read = await Promise.all(incoming.slice(0, room).map((f) => fileToBase64(f)));
      setFiles((prev) => [...prev, ...read]);
    } catch (e) { toast.error(e.message || "Couldn't read that image."); }
  };

  const derive = async () => {
    const t = (transcript || "").trim();
    if (t.length < 15) { toast("Dictate about the wound first, then pull the site & context."); return; }
    setDeriving(true);
    try {
      const d = await deriveWoundContext(t);
      if (d.wound_site) setWoundSite(d.wound_site);
      if (d.context) setContext(d.context);
      if (!d.wound_site && !d.context) toast("Nothing about a wound was found in the transcript yet.");
      else toast.success("Pre-filled from the transcript — review before analyzing.");
    } catch (e) { toast.error(describeError(e)); }
    finally { setDeriving(false); }
  };

  const analyze = async (engineOverride) => {
    if (!files.length) { toast.error("Add at least one wound photo."); return; }
    const eng = engineOverride || engine;
    setAnalyzing(true); setBlocked(false);
    try {
      const body = {
        images: files.map((f) => f.base64),
        engine: eng,
        wound_site: woundSite || undefined,
        context: context || undefined,
        reference_marker: refMarker || undefined,
        manual_length_cm: num(mL), manual_width_cm: num(mW), manual_depth_cm: num(mD),
        wound_label: label || undefined,
        // patient + session binding — always sent from a dictation session
        mrn: ctx?.mrn || undefined,
        chart_id: ctx?.chartId || undefined,
        chart_uuid: ctx?.patientId || undefined,
        patient_name: patientName || undefined,
        dictation_session_id: sessionId || undefined,
        client_id: null,
      };
      const { job_id } = await submitWoundAnalysis(body);
      if (!job_id) throw new Error("The analysis didn't start.");
      const result = await pollWoundJob(job_id);
      if (result) { setAnalyses((prev) => [result, ...prev]); setActive(result); }
      // reset the capture form (keep engine choice)
      setFiles([]); setWoundSite(""); setContext(""); setLabel(""); setRefMarker("");
      setML(""); setMW(""); setMD(""); setShowManual(false);
      toast.success("Wound documented — added to this session.");
      refresh();
    } catch (e) {
      if (is422(e)) { setBlocked(true); toast.error("This engine's safety filter blocked the image — try another engine."); }
      else toast.error(describeError(e));
    } finally { setAnalyzing(false); }
  };

  return (
    <div className="wc" data-testid="wound-panel">
      {/* ---- Capture ---- */}
      <div className="wc-cap">
        <div className="wc-title"><Camera size={15} /> Document a wound</div>
        <div className="wc-sub">Photograph the wound and Lumina Lens returns structured, objective findings. Up to 4 photos of the same wound.</div>

        <div className="wc-thumbs" data-testid="wound-thumbs">
          {files.map((f, i) => (
            <div className="wc-thumb" key={i}>
              <img src={f.preview} alt={`wound ${i + 1}`} />
              <button className="wc-thumb-x" onClick={() => setFiles((p) => p.filter((_, j) => j !== i))} aria-label="Remove photo"><X size={12} /></button>
            </div>
          ))}
          {files.length < 4 && (
            <div className="wc-adds">
              <button className="wc-add" onClick={() => camRef.current?.click()} data-testid="wound-camera"><Camera size={16} /> Camera</button>
              <button className="wc-add" onClick={() => fileRef.current?.click()} data-testid="wound-upload"><ScanLine size={16} /> Upload</button>
            </div>
          )}
          <input ref={camRef} type="file" accept="image/*" capture="environment" hidden onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }} />
          <input ref={fileRef} type="file" accept="image/*" multiple hidden onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }} />
        </div>

        <div className="wc-fields">
          <div className="wc-fld">
            <label>Wound site</label>
            <input value={woundSite} onChange={(e) => setWoundSite(e.target.value)} placeholder="e.g. Left lateral malleolus" data-testid="wound-site" />
          </div>
          <div className="wc-fld">
            <label>Display label <span className="wc-opt">optional</span></label>
            <input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="e.g. Left ankle ulcer" data-testid="wound-label" />
          </div>
        </div>
        <div className="wc-fld">
          <div className="wc-fld-row">
            <label>Context</label>
            <button className="wc-derive" onClick={derive} disabled={deriving} data-testid="wound-derive">
              {deriving ? <Loader2 size={12} className="lumspin" /> : <Sparkles size={12} />} Pull from transcript
            </button>
          </div>
          <textarea value={context} onChange={(e) => setContext(e.target.value)} rows={2} placeholder="Etiology, duration, treatment to date…" data-testid="wound-context" />
        </div>

        <button className="wc-manual-toggle" onClick={() => setShowManual((s) => !s)} data-testid="wound-manual-toggle">
          <Ruler size={13} /> {showManual ? "Hide" : "Add"} clinician measurements
          <span className="wc-opt">the only path to trustworthy absolute cm</span>
        </button>
        {showManual && (
          <div className="wc-manual" data-testid="wound-manual">
            <div className="wc-mgrid">
              <div className="wc-fld"><label>Length cm</label><input inputMode="decimal" value={mL} onChange={(e) => setML(e.target.value)} placeholder="3.2" data-testid="wound-len" /></div>
              <div className="wc-fld"><label>Width cm</label><input inputMode="decimal" value={mW} onChange={(e) => setMW(e.target.value)} placeholder="1.8" data-testid="wound-wid" /></div>
              <div className="wc-fld"><label>Depth cm</label><input inputMode="decimal" value={mD} onChange={(e) => setMD(e.target.value)} placeholder="0.4" data-testid="wound-dep" /></div>
            </div>
            <div className="wc-fld">
              <label>Reference marker in photo <span className="wc-opt">optional</span></label>
              <input value={refMarker} onChange={(e) => setRefMarker(e.target.value)} placeholder="e.g. ruler in frame, cm side up" data-testid="wound-ref" />
            </div>
            <div className="wc-note">Depth and true wound-bed area are never estimated from a photo — only what you type here is treated as authoritative.</div>
          </div>
        )}

        <div className="wc-engine">
          <label>Engine</label>
          <select value={engine} onChange={(e) => setEngine(e.target.value)} data-testid="wound-engine">
            {WOUND_ENGINES.map((e) => <option key={e.value} value={e.value}>{e.value.charAt(0).toUpperCase() + e.value.slice(1)}</option>)}
          </select>
        </div>

        {blocked && (
          <div className="wc-blocked" data-testid="wound-blocked">
            <ShieldAlert size={15} />
            <div>
              <b>This engine's content-safety filter blocked the image.</b> This isn't an error — some graphic wounds trip a model's filter. Try a different engine:
              <div className="wc-blocked-btns">
                {WOUND_ENGINES.filter((e) => e.value !== engine).map((e) => (
                  <button key={e.value} className="btn" onClick={() => { setEngine(e.value); analyze(e.value); }} disabled={analyzing} data-testid={`wound-switch-${e.value}`}>{e.label.replace("Lumina Lens ", "")}</button>
                ))}
              </div>
            </div>
          </div>
        )}

        <button className="btn primary wc-go" onClick={() => analyze()} disabled={analyzing || !files.length} data-testid="wound-analyze">
          {analyzing ? <><Loader2 size={14} className="lumspin" /> Analyzing…</> : <><Sparkles size={14} /> Analyze wound</>}
        </button>
        <div className="honest wc-disc">Lumina-assisted documentation only — not a diagnosis. The photo itself is never stored; only these structured findings are.</div>
      </div>

      {/* ---- This session's wounds ---- */}
      {analyses.length > 0 && (
        <div className="wc-list" data-testid="wound-list">
          <div className="wc-list-h">Captured this session <span className="tag">{analyses.length}</span></div>
          {analyses.map((a) => <WoundSummaryRow key={a.id || a.created_at} a={a} onOpen={() => setActive(a)} />)}
        </div>
      )}

      {/* ---- Cross-visit progression ---- */}
      <Progression groups={groups} />

      {/* ---- Full-findings review modal ---- */}
      {active && (
        <div className="wc-modal-back" onClick={() => setActive(null)} data-testid="wound-modal">
          <div className="wc-modal" onClick={(e) => e.stopPropagation()}>
            <div className="wc-modal-h">
              <span>Wound findings — review before signing</span>
              <button className="wc-modal-x" onClick={() => setActive(null)} aria-label="Close" data-testid="wound-modal-close"><X size={18} /></button>
            </div>
            <div className="wc-modal-body"><WoundResultCard a={active} wide /></div>
          </div>
        </div>
      )}
    </div>
  );
}

// One compact, scannable row per wound — the heavy findings open in the modal.
function WoundSummaryRow({ a, onOpen }) {
  const r = a?.result || {};
  const dims = r.dimensions || {};
  const seg = r.segmentation || {};
  const calibrated = dims.measurement_basis === "clinician_entered" || dims.measurement_basis === "measured_with_reference";
  const size = calibrated && dims.area_cm2 != null ? `${dims.area_cm2} cm²`
    : seg.area_image_fraction != null ? `${pct(seg.area_image_fraction)} of frame` : null;
  const meta = [r.suspected_stage, r.confidence && `${r.confidence} confidence`, fmtTime(a.created_at)].filter(Boolean).join(" · ");
  return (
    <button className="wc-row" onClick={onOpen} data-testid="wound-row">
      <div className="wc-row-main">
        <div className="wc-row-title">{a.wound_label || a.wound_site || r.wound_location || "Wound"}</div>
        <div className="wc-row-meta">{meta || "Tap to review findings"}</div>
      </div>
      {size && <span className="wc-row-size">{size}</span>}
      <ChevronRight size={16} className="wc-row-chev" />
    </button>
  );
}

function bar(label, v, cls) {
  if (v == null) return null;
  return (
    <div className="wc-tissue-row" key={label}>
      <span className="wc-tissue-l">{label}</span>
      <span className="wc-tissue-track"><span className={`wc-tissue-fill ${cls}`} style={{ width: `${v}%` }} /></span>
      <span className="wc-tissue-v">{v}%</span>
    </div>
  );
}

function WoundResultCard({ a }) {
  const r = a?.result || {};
  const dims = r.dimensions || {};
  const basis = dims.measurement_basis;
  const calibrated = basis === "clinician_entered" || basis === "measured_with_reference";
  const seg = r.segmentation || {};
  const tc = r.tissue_composition || {};
  const inf = Array.isArray(r.signs_of_infection) ? r.signs_of_infection : [];
  const hasTissue = tc.granulation_pct != null || tc.slough_pct != null || tc.eschar_pct != null || tc.epithelial_pct != null;

  const sizeGlance = calibrated
    ? [dims.length_cm, dims.width_cm].every((v) => v != null)
      ? `${dims.length_cm} × ${dims.width_cm} cm${dims.area_cm2 != null ? ` · ≈${dims.area_cm2} cm²` : ""}`
      : dims.area_cm2 != null ? `≈${dims.area_cm2} cm²` : "—"
    : pct(seg.area_image_fraction) != null ? `${pct(seg.area_image_fraction)} of frame` : "—";
  const exudateGlance = r.exudate && (r.exudate.amount || r.exudate.type)
    ? [r.exudate.amount, r.exudate.type].filter(Boolean).join(", ") : "None noted";

  return (
    <div className="wc-card" data-testid="wound-result">
      {/* identity */}
      <div className="wc-hero">
        <div className="wc-hero-txt">
          <div className="wc-card-title">{a.wound_label || a.wound_site || r.wound_location || "Wound"}</div>
          {(a.wound_site || r.wound_location) && <div className="wc-hero-site">{a.wound_site || r.wound_location}</div>}
        </div>
        <span className={`status ${r.confidence === "high" ? "st-ink" : r.confidence === "low" ? "st-amber" : "st-neutral"}`}>{r.confidence || "—"} confidence</span>
      </div>
      <div className="wc-card-meta">{a.engine_label || a.engine} · {fmtTime(a.created_at)}{r.image_quality ? ` · image: ${r.image_quality}` : ""}</div>

      {r.is_wound === false && <div className="wc-flag-warn"><ShieldAlert size={14} /> The engine did not identify a wound in this photo.</div>}

      {/* AT A GLANCE — fixed row a physician checks first */}
      <div className="wc-glance" data-testid="wound-glance-strip">
        <div className="wc-stat"><div className="wc-stat-l">Stage</div><div className="wc-stat-v">{r.suspected_stage || "—"}</div></div>
        <div className="wc-stat"><div className="wc-stat-l">Size</div><div className="wc-stat-v">{sizeGlance}{!calibrated && sizeGlance !== "—" && <span className="wc-rel">relative</span>}</div></div>
        <div className="wc-stat"><div className="wc-stat-l">Exudate</div><div className="wc-stat-v">{exudateGlance}</div></div>
        <div className={`wc-stat ${inf.length ? "wc-stat-alert" : "wc-stat-ok"}`}><div className="wc-stat-l">Infection signs</div><div className="wc-stat-v">{inf.length ? `${inf.length} noted` : "None noted"}</div></div>
      </div>

      {/* sections — consistent order every time */}
      <div className="wc-sections">
        <Section icon={<Ruler size={13} />} title="Measurements" tid="wound-measurements-section">
          <div className={`wc-calib ${calibrated ? "ok" : "rel"}`} data-testid="wound-calibration">
            {calibrated ? "Calibrated — comparable across visits" : "Relative — not trackable across visits"}
          </div>
          {calibrated ? (
            <>
              <div className="wc-dims">
                {dims.length_cm != null && <span><b>{dims.length_cm}</b> cm L</span>}
                {dims.width_cm != null && <span><b>{dims.width_cm}</b> cm W</span>}
                {dims.depth_cm != null ? <span><b>{dims.depth_cm}</b> cm depth</span> : <span className="wc-muted">depth — enter manually</span>}
              </div>
              {dims.area_cm2 != null && <div className="wc-area">≈ <b>{dims.area_cm2}</b> cm² <span className="wc-opt">L × W estimate</span></div>}
              <div className="wc-note">{basis === "clinician_entered" ? "Clinician-measured" : "Scale in photo"}{dims.reference_used ? ` · ${dims.reference_used}` : ""} — comparable across visits.</div>
            </>
          ) : (
            <>
              <div className="wc-dims">
                {pct(seg.area_image_fraction) != null && <span><b>{pct(seg.area_image_fraction)}</b> of frame</span>}
                {pct(seg.longest_axis_frac) != null && <span><b>{pct(seg.longest_axis_frac)}</b> longest axis</span>}
              </div>
              <div className="wc-note">No scale in photo — relative to <i>this photo only</i>, not comparable between visits. Add a ruler or measurements for trackable cm.</div>
            </>
          )}
        </Section>

        <Section icon={<Activity size={13} />} title="Tissue in wound bed" tid="wound-tissue-section">
          {hasTissue ? (
            <>
              {bar("Granulation", tc.granulation_pct, "wc-gran")}
              {bar("Slough", tc.slough_pct, "wc-slough")}
              {bar("Eschar", tc.eschar_pct, "wc-eschar")}
              {bar("Epithelial", tc.epithelial_pct, "wc-epi")}
            </>
          ) : <div className="wc-muted">Not described.</div>}
        </Section>

        <Section title="Wound bed & margins" tid="wound-bed-margins-section">
          <KV k="Wound bed" v={r.wound_bed} />
          <KV k="Edges" v={r.wound_edges} />
          <KV k="Periwound skin" v={r.periwound_skin} />
        </Section>

        <Section title="Exudate & infection" tid="wound-exudate-infection-section">
          <KV k="Exudate" v={r.exudate && (r.exudate.amount || r.exudate.type) ? [r.exudate.amount, r.exudate.type].filter(Boolean).join(", ") : ""} />
          <div className={`wc-kv ${inf.length ? "wc-kv-alert" : ""}`}><span className="wc-k">Infection signs</span><span>{inf.length ? inf.join(", ") : "none noted"}</span></div>
        </Section>
      </div>

      {/* impression */}
      <div className="wc-impression" data-testid="wound-impression-section">
        <div className="wc-block-h">Impression</div>
        <div className="wc-narrative">{r.narrative || "No narrative summary returned."}</div>
      </div>

      <div className="honest wc-disc">{(r.disclaimer || "Lumina-assisted documentation only — not a diagnosis.").replace(/\bAI[- ]generated\b/gi, "Lumina-generated")}</div>
    </div>
  );
}

function Section({ icon, title, children, tid }) {
  return (
    <div className="wc-block" data-testid={tid}>
      <div className="wc-block-h">{icon}{title}</div>
      {children}
    </div>
  );
}
function KV({ k, v }) {
  return <div className="wc-kv"><span className="wc-k">{k}</span><span className={v ? "" : "wc-muted"}>{v || "not described"}</span></div>;
}

function Progression({ groups }) {
  const tracked = (groups || []).filter((g) => (g.count || 0) >= 2);
  if (!tracked.length) return null;
  return (
    <div className="wc-list" data-testid="wound-progression">
      <div className="wc-list-h">Progression <span className="wc-opt">across this patient's visits</span></div>
      {tracked.map((g) => {
        const eligible = g.comparison_eligible && g.area_cm2_delta != null;
        const down = eligible && g.area_cm2_delta < 0;
        return (
          <div className="wc-prog" key={g.wound_key}>
            <div className="wc-prog-h">
              <span className="wc-prog-name">{g.wound_label || g.wound_key}</span>
              <span className="tag">{g.count} photos</span>
            </div>
            <div className="wc-prog-meta">{fmtTime(g.first_at)} → {fmtTime(g.last_at)}</div>
            {eligible ? (
              <div className={`wc-prog-delta ${down ? "down" : "up"}`}>
                {down ? <TrendingDown size={15} /> : <TrendingUp size={15} />}
                {down ? "▼" : "▲"} {Math.abs(g.area_cm2_delta)} cm² area {down ? "smaller" : "larger"} since first calibrated measure
              </div>
            ) : (
              <div className="wc-note">{g.comparison_note || "Need at least two calibrated measurements (same method) to show a trend."}</div>
            )}
          </div>
        );
      })}
    </div>
  );
}
